/**
 * @module unified-data/unifieddata
 */

import {Plugin} from '@oracle/ortl-core';
import SourceEditing from '../../source-editing/src/sourceediting.js';

/**
 * The source editing feature.
 *
 * @extends module:core/plugin~Plugin
 */
export default class UnifiedData extends Plugin {
	/**
	 * @inheritDoc
	 */
	static get pluginName() {
		return 'UnifiedData';
	}

	/**
	 * @inheritDoc
	 */
	static get requires() {
		return [SourceEditing];
	}

	get sourceEditor() {
		return this.editor.plugins.get('SourceEditing');
	}

	/**
	 * @inheritDoc
	 */
	constructor(editor) {
		super(editor);

		/**
		 * Maps all root names to wysiwyg data
		 * @type {Map<string, string>}
		 * @private
		 */
		this._wysiwygData = new Map();

		/**
		 * Maps all root names to their real data
		 * @type {Map<string, string>}
		 * @private
		 */
		this._data = new Map();

		this._onWysiwygDataChange = this._onWysiwygDataChange.bind(this);
		this._onSourceDataChange = this._onSourceDataChange.bind(this);
	}

	/**
	 * @inheritDoc
	 */
	init() {
		const editor = this.editor;
		const sourceEditor = this.sourceEditor;

		editor.config.set('sourceEditing', {
			loadData: this._loadDataToSourceEditor.bind(this),
			saveData: this._saveDataFromSourceEditor.bind(this),
		});

		// Update the editor data while calling editor.getData() in the source editing mode.
		editor.data.on(
			'get',
			() => {
				this._updateWysiwygData();
			},
			{priority: 'high'},
		);

		editor.on('ready', () => {
			const initialData = editor.config.get('initialData');
			this._data.set('main', initialData);
			this._wysiwygData.set('main', initialData);
			editor.model.document.on('change:data', this._onWysiwygDataChange);
		});
		sourceEditor.on('change:data', this._onSourceDataChange);
		sourceEditor.on('set:isSourceEditingMode', this._onSourceEditingModeChange.bind(this));
	}

	_onSourceEditingModeChange(evt, property, newIsEditing, oldIsEditing) {
		if (!newIsEditing && oldIsEditing && !this._validate()) {
			evt.return = true;
			evt.stop();
		}
	}

	_onSourceDataChange(evt, property, data, rootName) {
		this._data.set(rootName, data);
		this.fire('change:data', 'data', data, rootName);
	}

	_onWysiwygDataChange() {
		const sourceEditor = this.sourceEditor;
		const rootName = 'main';
		const data = this.editor.data.get({rootName});
		this._data.set(rootName, data);
		this._wysiwygData.set(rootName, data);
		if (sourceEditor.isSourceEditingMode) {
			sourceEditor.off('change:data', this._onSourceDataChange);
			sourceEditor.setData(data, rootName);
			sourceEditor.on('change:data', this._onSourceDataChange);
		}
		this.fire('change:data', 'data', data, rootName);
	}

	/**
	 * Updates the source data in all hidden editing roots.
	 *
	 * @private
	 */
	_updateWysiwygData() {
		const editor = this.editor;
		const dataToSave = {};

		this._data.forEach((newData, rootName) => {
			if (this._wysiwygData.get(rootName) !== newData) {
				dataToSave[rootName] = newData;
				this._wysiwygData.set(rootName, newData);
			}
		});

		if (Object.keys(dataToSave).length) {
			editor.model.document.off('change:data', this._onWysiwygDataChange);
			try {
				editor.data.set(dataToSave, {batchType: {isUndoable: false}});
			} catch (e) {
				editor.editing.view.isRenderingInProgress = false;
				editor.data.set('', {batchType: {isUndoable: false}});
				this._wysiwygData.clear();
				return false;
			} finally {
				editor.model.document.on('change:data', this._onWysiwygDataChange);
			}
		}
		return true;
	}

	_updateSourceData() {
		const sourceEditor = this.sourceEditor;
		sourceEditor.off('change:data', this._onSourceDataChange);
		this._data.forEach((data, rootName) => {
			sourceEditor.setData(data, rootName);
		});
		sourceEditor.on('change:data', this._onSourceDataChange);
		return true;
	}

	_loadDataToSourceEditor(rootName) {
		const data = this._data.get(rootName);
		const isHtml = this.sourceEditor.config.language === 'html';
		return isHtml && !SourceEditing.isFormattedHtml(data) ? SourceEditing.formatHtml(data) : data;
	}

	_saveDataFromSourceEditor(data) {
		this._updateWysiwygData();
	}

	/**
	 * Insert text at cursor position
	 * @param string text
	 * @param string [rootName='main']
	 */
	insertData(data, rootName = 'main') {
		const sourceEditor = this.sourceEditor;
		if (sourceEditor.isSourceEditingMode) {
			sourceEditor.insertData(data, rootName);
		} else {
			const editor = this.editor;
			editor.model.change((writer) => {
				const viewFragment = editor.data.processor.toView(data);
				const modelFragment = editor.data.toModel(viewFragment);
				const insertPosition = editor.model.document.selection.getFirstPosition();
				editor.model.insertContent(modelFragment, insertPosition);
			});
		}
	}

	/**
	 * Get content of editor
	 * @param {string} rootName
	 * @return {string}
	 */
	getData(rootName = 'main') {
		return this._data.get(rootName);
	}

	/**
	 * Sets entire content of editor
	 * @param {string} data
	 * @param {string} rootName
	 */
	setData(data, rootName = 'main') {
		if (this._data.get(rootName) !== data) {
			this._data.set(rootName, data);
			if (this.sourceEditor.isSourceEditingMode) {
				this._updateSourceData();
			} else {
				if (this._validate()) {
					this._updateWysiwygData();
				} else {
					this.sourceEditor.isSourceEditingMode = true;
				}
			}
			this.fire('change:data', 'data', data, rootName);
		}
	}

	_validate() {
		const valid = this._updateWysiwygData();
		this.sourceEditor._showErrorMessage(!valid);
		return valid;
	}
}
